import { Component, Inject, OnDestroy, Type, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegisterClass } from '@memberjunction/global';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { Metadata } from '@memberjunction/core';
import { ResourceData } from '@memberjunction/core-entities';
import {
    AGU_AD_ROTATION_STORE,
    AGU_APP_MODULES,
    AGU_APP_SURFACE_CONFIG,
    AGUAdPlacement,
    AGUAdSessionService,
    AGUAppComposition,
    AGUAppLocation,
    AGUAppModuleDefinition,
    AGUCrumbItem,
    AGUCrumbService,
    AGUSidebarIdentity,
    AGUSidebarPromo,
    AGUSurfaceReadyService,
    AGUToastService,
    AGUViewportService,
    BaseAGUAppSurfaceComponent,
    AGURenderService,
    CurrentUserRoleNames,
    DecodeLocationParams,
    DecodeLocationParamsFromSearch,
    DescribeAppLocation,
    DetailAdScreen,
    FirstAncestor,
    FlattenModuleRegistry,
    HideTabsOnScroll,
    IsAGUAdKind,
    TAB_SCROLL_JITTER_PX,
    ParseAppComposition,
    PickLandingModule,
    ResolveComposedModules,
    WithMountedModule,
    type UserActivityPageDescriptor,
} from 'agu_app_shell';
import { ExplorerRaHostAdapter, RA_SURFACE_CONFIG } from './ra-explorer-host.adapter';
import { ComposeRoleLine } from './ra-role-line';
import { SidebarIdentityFrom } from './ra-sidebar-identity';
import { RaCompositionState } from './ra-composition.state';
import { CurrentUserCanOpenApp } from './ra-app-access';
import { RA_MODULE_DEFINITIONS } from './ra-module-definitions';
import { RA_MODULE_KEYS } from './ra-module-keys';
import { ResearchApiService } from './services/research-api.service';
import { MemberItemStateService } from './services/member-item-state.service';
import { PortalConfigService } from './services/portal-config.service';
import { LapsedBannerText, MembershipService } from './services/membership.service';
import { RaAdRotationStore } from './ra-ad-rotation.store';
import { ResearchAdPlacement, ResearchPortalConfig } from './models/research.models';

/**
 * Whether the surface's scrollport goes back to the top for this location change.
 *
 * `mj-agu-surface` IS the scrollport (the immersive mixin gives it `height: 100%;
 * overflow-y: auto`) and it outlives every module, because modules stay mounted
 * (`WithMountedModule`). Nothing collapses on navigation, so without a reset a
 * member lands wherever the previous screen was scrolled to.
 *
 * - Moving to another module: reset.
 * - Inside a module, opening a view that is NOT the module's `DefaultView` and is
 *   a different (View, RecordID) from where the member was — a grant/job detail,
 *   a collaborator profile, a second record's detail: reset, so the title is on
 *   screen (2026-10-05 stage sanity: profile 279422 opened at `scrollTop` 269).
 * - Back to the module's DefaultView (list, board), or the same view and record
 *   again (a re-emission): left alone.
 *
 * `previous` is null before the first location, which always resets.
 * `nextDefaultView` is null when the module is not in this composition.
 */
export function ShouldResetSurfaceScroll(previous: AGUAppLocation | null, next: AGUAppLocation, nextDefaultView: string | null): boolean {
    if (previous === null || previous.ModuleKey !== next.ModuleKey) return true;
    if (next.View === nextDefaultView) return false;
    return previous.View !== next.View || (previous.RecordID ?? null) !== (next.RecordID ?? null);
}

/**
 * Whether a location is a detail view — a grant or job detail, or a person's profile in the people finder — which the phone shell
 * shows without its bottom tabs (`html.mob.mdetail .mtabs { display:none }`, P/Member_Hub.html:515; a screen is a detail when it has
 * `.detail`, :858-859). The member's own profile and the list, board and results views are not.
 */
export function IsDetailLocation(location: AGUAppLocation): boolean {
    if (location.View === 'detail') return true;
    return location.ModuleKey === RA_MODULE_KEYS.People && location.View === 'profile';
}

/**
 * The Featured-placement screen a location shows (PD §3): Home, the three finders, a detail as `detail:<module>` so the grant
 * detail takes the house display creative (`SelectPlacements`), and every other module under its own key — which no seeded
 * placement lists, so Career Explorer, Saved, Profile, Search and Settings carry none.
 */
export function RaAdScreen(location: AGUAppLocation): string {
    if (IsDetailLocation(location)) return DetailAdScreen(location.ModuleKey);
    switch (location.ModuleKey) {
        case RA_MODULE_KEYS.Dashboard: return 'home';
        case RA_MODULE_KEYS.Grants: return 'grants';
        case RA_MODULE_KEYS.People: return 'people';
        case RA_MODULE_KEYS.Jobs: return 'jobs';
        default: return location.ModuleKey;
    }
}

/** The server's placements with `kind` narrowed; an entry of a kind this client cannot mount is left out. */
export function ToAdPlacements(rows: readonly ResearchAdPlacement[]): AGUAdPlacement[] {
    const placements: AGUAdPlacement[] = [];
    for (const row of rows) {
        const kind = row.kind;
        if (IsAGUAdKind(kind)) placements.push({ ...row, kind });
    }
    return placements;
}

/** The phone bottom tabs, by module key: Home / Grants / People / Jobs / Saved (P/Member_Hub.html:846-852). */
export const RA_PHONE_TAB_KEYS: readonly string[] = [RA_MODULE_KEYS.Dashboard, RA_MODULE_KEYS.Grants, RA_MODULE_KEYS.People, RA_MODULE_KEYS.Jobs, RA_MODULE_KEYS.Saved];

/** The bottom tabs this member gets: the resolved modules whose key is a tab key, in the composition's nav order. */
export function PhoneTabModules(modules: readonly AGUAppModuleDefinition[]): readonly AGUAppModuleDefinition[] {
    return modules.filter((m) => RA_PHONE_TAB_KEYS.includes(m.Key));
}

/** The Science Talent Network module's copy, verbatim from P/Member_Hub.html:1717. One object, so the sidebar's `Promo` input keeps its identity across change detection. */
const STN_PROMO: AGUSidebarPromo = {
    Eyebrow: 'Science Talent Network',
    Title: 'Get discovered',
    Text: 'Your profile is part of the Science Talent Network. Keep it current to put your best foot forward.',
    CtaLabel: 'Update profile',
};

/**
 * The one surface behind every AGU Research Accelerator app.
 *
 * Driver class 'AGUAppSurface' is shared by four Dashboard records — Grant
 * Finder, People Finder, Job Finder and the composite Research Accelerator — and
 * what distinguishes them is DATA: each record's UIConfigDetails names the
 * modules its app is made of. A fifth app is a fifth metadata row and no code
 * (ui-best-practices §7, "behavior config is data, not code").
 *
 * CONFIG TIMING. The Explorer container assigns Config synchronously right after
 * creating the component and then calls Refresh() → loadData(), while Angular
 * defers ngOnInit — and therefore initDashboard — to the next change-detection
 * tick (ng-explorer-core's dashboard-resource.component: `instance.Config =
 * config; … instance.Refresh();`). So Config is always readable by the time
 * initDashboard runs, but loadData can run BEFORE it. loadData is therefore a
 * no-op here, and nothing outside InitializeSession may assume the composition
 * has resolved.
 *
 * Fails closed at every gate: an unconfigured or malformed composition, a
 * composition naming modules whose code is not deployed, and a user entitled to
 * none of the app's modules each render an explicit state rather than an empty
 * or defaulted app.
 */
@Component({
    standalone: false,
    selector: 'mj-agu-surface',
    templateUrl: './ra-surface.component.html',
    styleUrls: ['./ra-surface.component.scss'],
    /**
     * Buys specificity for the surface's global overrides, nothing more. MJ's
     * chrome components use emulated encapsulation, compiling `:host` to
     * `[_nghost-x]` (0,1,0) and inner rules to `.cls[_ngcontent-x]` (0,2,0); a
     * descendant selector written from styles.scss as `mj-agu-surface .cls` is
     * only (0,1,1) and loses to both. The host class lets every override carry
     * one more class, so no `!important` is ever needed.
     */
    host: {
        class: 'agu-scope',
        // No overflow binding here: the phone drawer and filter sheet lock this scrollport themselves (`ModalFocusController`).
    },
    /**
     * Per-surface providers, on the COMPONENT rather than RaSurfaceModule. Every Explorer surface
     * module is imported eagerly into AppModule, so NgModule providers all land in the ONE root
     * injector: listed on the module, this adapter (and its Router listeners), the RA surface config
     * and the data/state services were app-wide singletons for the whole session — shared by every
     * RA tab and reachable from any other surface (an OEP view injecting AGU_APP_SURFACE_CONFIG got
     * RA's). On this element injector each surface instance gets its own set, every module rendered
     * under it resolves them by walking the element tree, and they are destroyed with the surface.
     * Ownership is pinned by ra-surface.providers.spec.ts.
     */
    providers: [
        { provide: AGU_APP_SURFACE_CONFIG, useValue: RA_SURFACE_CONFIG },
        ExplorerRaHostAdapter,
        RaCompositionState,
        // The module registry. `multi: true` is what lets a future feature area
        // contribute its own modules without editing this array — the surface
        // flattens every contribution and composes by metadata.
        { provide: AGU_APP_MODULES, useValue: RA_MODULE_DEFINITIONS, multi: true },
        // Data transport and the member's per-session memos (profile, portal
        // config, saved/hidden item state).
        ResearchApiService,
        MemberItemStateService,
        PortalConfigService,
        // The member's membership state (token claims): the lapsed banner here, People Finder's lock, Home's CV module, Career Explorer's lock (Task 28).
        MembershipService,
        // The surface's repaint boundary. MJExplorer runs zoneless (Angular 21
        // binds NgZone to NoopNgZone for every `bootstrapModule` app), so an
        // async load finishing schedules no change detection on its own — see
        // render.service.ts (agu_app_shell) for the framework trace.
        AGURenderService,
        // Phone or not, live (`matchMedia(PHONE_MEDIA_QUERY)`); per surface so its listener dies with the surface.
        AGUViewportService,
        // The phone toast (P/Member_Hub.html:952-966): one per surface, rendered once in the template, reached by every module's
        // save handler through the element injector.
        AGUToastService,
        // The trail the detail on screen shows (P/detail.html:888); the phone app bar reads its first ancestor (P/Member_Hub.html:862-868).
        AGUCrumbService,
        // The Featured placements' session state (PD §3): enabled/placements from the portal config, the member's dismissals
        // (in memory, per surface), and the Home feature rotation index, kept as a MJ user setting through UserInfoEngine.
        AGUAdSessionService,
        { provide: AGU_AD_ROTATION_STORE, useClass: RaAdRotationStore },
    ],
})
@RegisterClass(BaseDashboard, 'AGUAppSurface')
export class RaSurfaceComponent extends BaseAGUAppSurfaceComponent<AGUAppLocation> implements OnDestroy {
    override location: AGUAppLocation = { ModuleKey: '', View: '' };

    /** False until the readiness gate resolves — drives the loading state. */
    ready = false;
    /** Set when the composition or readiness failed; drives the error state. */
    loadError: string | null = null;
    /**
     * Set when `researchAccess` returns `denied` — no AGU member record is
     * linked to this account. Drives the lock state instead of the shell; the
     * only account-eligibility state stronger than an unresolved role
     * (`modules.length === 0`), since a denied account has no member/staff
     * context at all for any module to use.
     */
    accessDenied: string | null = null;
    /**
     * True when the signed-in user's roles do not open this dashboard's MJ Application. Checked
     * before anything else loads, because Explorer can mount this surface from a saved workspace
     * tab without its own app-access check (see `CurrentUserCanOpenApp`).
     */
    appAccessDenied = false;

    /** The amber lapsed-membership strip's sentence (P/proto-states.js:134); null — nothing rendered — unless the token says lapsed. */
    get LapsedBanner(): string | null { return this.membership.IsLapsed ? LapsedBannerText(this.membership.EndsAt) : null; }

    /** The strip's Renew button opens the AGU account page. */
    OnRenew(): void { void this.membership.OpenRenewal(this.portalConfig); }

    /** Modules of this app the signed-in user may actually reach, in nav order. */
    modules: readonly AGUAppModuleDefinition[] = [];

    /**
     * Modules the member has actually opened, in first-visit order — the list
     * the template renders. Every one of them stays MOUNTED for the life of the
     * surface and is hidden when another is active, so a module keeps the state
     * it built up (a finder's query, filters, sort, page and results) instead of
     * being destroyed and re-created on every sidebar navigation, which is what
     * live finding #19 / suite J-6 caught. Modules that refresh on re-entry
     * (Home, Saved) do it themselves off `Location$` — see `IsModuleReentry`.
     */
    mountedModules: readonly AGUAppModuleDefinition[] = [];

    /**
     * The location before the one now in `location` — the base class assigns
     * `location` BEFORE calling `OnLocationChanged`, so the previous value has
     * to be kept here for `ShouldResetSurfaceScroll`.
     */
    private previousLocation: AGUAppLocation | null = null;

    /** Public for the template (header outputs, empty-state actions). */
    readonly hostAdapter: ExplorerRaHostAdapter;
    protected override readonly Adapter: ExplorerRaHostAdapter;

    private composition: AGUAppComposition | null = null;
    private readonly registry: readonly AGUAppModuleDefinition[];
    /**
     * The sidebar's role line ("Research Scientist · NOAA"), from the member's
     * own profile. Null until `LoadRoleLine` resolves, and permanently null for
     * staff — a staff account has no member record, so there is no job title or
     * organization to state and the sidebar omits the line entirely.
     */
    private roleLine: string | null = null;
    /**
     * The sidebar identity block's name/initials source (M-1b) — the
     * server-supplied `researchMyProfile.memberName`, loaded in the same
     * round trip as `roleLine` (`LoadRoleLine`), for members AND staff (B-20).
     * Null until that resolves, and permanently null when neither the
     * AguPerson row nor the MJ user has a usable name, in which case
     * `SidebarIdentityFrom` falls back to the MJ user's own name parts and
     * then the generic placeholder — never an e-mail.
     */
    private memberName: string | null = null;

    /** Whether the phone shell (app bar, bottom tabs, drawer) is on, from `AGUViewportService`. */
    IsPhone: boolean;
    /** Whether the phone drawer is open. */
    drawerOpen = false;
    /** Whether the bottom tabs are tucked away by a scroll down (`HideTabsOnScroll`). */
    tabsHidden = false;
    /** The bottom tabs' modules, fixed once the composition resolves so the input keeps its identity. */
    phoneTabs: readonly AGUAppModuleDefinition[] = [];
    /** The scroll position the tab rule last counted (P/Member_Hub.html:968-975). */
    private lastScrollY = 0;
    private readonly viewportSubscription: Subscription;
    /** The trail the detail views publish (`AGUCrumbService`), for the phone app bar. */
    private crumbTrail: readonly AGUCrumbItem[] = [];
    private readonly crumbSubscription: Subscription;
    private readonly onSurfaceScroll = (): void => this.OnSurfaceScroll();

    constructor(
        hostAdapter: ExplorerRaHostAdapter,
        private readonly readyService: AGUSurfaceReadyService,
        private readonly compositionState: RaCompositionState,
        private readonly api: ResearchApiService,
        private readonly portalConfig: PortalConfigService,
        readonly membership: MembershipService,
        private readonly render: AGURenderService,
        viewport: AGUViewportService,
        toast: AGUToastService,
        crumbs: AGUCrumbService,
        @Inject(AGU_APP_MODULES) moduleContributions: AGUAppModuleDefinition[][]
    ) {
        super();
        this.hostAdapter = hostAdapter;
        this.Adapter = hostAdapter;
        this.registry = FlattenModuleRegistry(moduleContributions);
        // Registered in the constructor, not ngOnInit: BaseDashboard's own
        // ngOnInit kicks InitializeSession, and the first module can finish its
        // load before any later hook would have run.
        this.render.Attach(this.Cdr);
        this.IsPhone = viewport.IsPhone;
        // The Saved toast's "View" (P/Member_Hub.html:955): the Saved board, when this app has one (`OpenModule` refuses otherwise).
        toast.OpenSaved = () => this.OpenModule(RA_MODULE_KEYS.Saved, 'board');
        // The media-query 'change' arrives outside any Angular notification on this zoneless host, so a flip asks for its repaint.
        this.viewportSubscription = viewport.IsPhone$.subscribe((isPhone) => {
            if (isPhone === this.IsPhone) return;
            this.IsPhone = isPhone;
            if (!isPhone) {
                // Leaving the phone layout closes the drawer (P/Member_Hub.html:828) and brings the tabs back for next time.
                this.drawerOpen = false;
                this.tabsHidden = false;
            }
            this.render.Notify();
        });
        // A detail publishes its trail when its record lands — an async load, so its repaint is asked for here.
        this.crumbSubscription = crumbs.Current$.subscribe((trail) => {
            // Empty to empty (the replay on subscribe, a detail withdrawing an unpublished trail) paints nothing new.
            const unchanged = trail.length === 0 && this.crumbTrail.length === 0;
            this.crumbTrail = trail;
            if (!unchanged) this.render.Notify();
        });
        // Passive and outside the template: a template listener would run change detection on every scroll event, and the rule
        // only repaints when the hidden state flips.
        this.HostElement.nativeElement.addEventListener('scroll', this.onSurfaceScroll, { passive: true });
    }

    override ngOnDestroy(): void {
        this.viewportSubscription.unsubscribe();
        this.crumbSubscription.unsubscribe();
        this.HostElement.nativeElement.removeEventListener('scroll', this.onSurfaceScroll);
        // A destroyed view must never be repainted — see AGURenderService.
        this.render.Detach(this.Cdr);
        // The memoised member profile dies with the surface. The service is
        // provided on this component, so the instance itself is released with
        // it; clearing here also drops the resolved profile promise from any
        // in-flight caller that still holds the service.
        this.api.ClearSessionCache();
        super.ngOnDestroy();
    }

    async GetResourceDisplayName(_data: ResourceData): Promise<string> {
        return this.AppName;
    }

    /** App name for the masthead: the Dashboard record's own name. */
    get AppName(): string {
        return this.Config?.dashboard?.Name?.trim() || 'AGU Research Accelerator';
    }

    /** Root component of the module currently on screen. */
    get ActiveModuleComponent(): Type<unknown> | null {
        return this.ActiveModule?.RootComponent ?? null;
    }

    get ActiveModule(): AGUAppModuleDefinition | null {
        return this.modules.find((m) => m.Key === this.location.ModuleKey) ?? null;
    }

    /**
     * Identity block the sidebar shows above the module nav.
     *
     * Name/initials come from `SidebarIdentityFrom` (M-1b, live round
     * 2026-09-12): the server-supplied `memberName` first — the same round
     * trip that already resolves `roleLine` — falling back to the
     * pre-existing `CurrentUser.Name` derivation, then the generic
     * placeholder. Before this fix, `Identity` derived name/initials from
     * `Metadata().CurrentUser` alone, which for a ForgeRock-provisioned
     * account is the sign-in e-mail — so a fully linked member (role line
     * correctly reading "Assistant Professor · Brigham Young University")
     * still saw the generic `AGU member` / `AG` in the name slot right above
     * it.
     */
    get Identity(): AGUSidebarIdentity | null {
        const user = new Metadata().CurrentUser;
        if (!user) return null;
        const { Name: name, Initials: initials } = SidebarIdentityFrom(this.memberName, user);
        return {
            Initials: initials,
            Name: name,
            RoleLine: this.roleLine,
            // Empty, not a label, when this app has no profile module: the link
            // would navigate nowhere (`OpenModule` refuses keys outside the
            // composition), and a dead link reads worse than no link.
            ProfileLinkLabel: this.HasModule('profile') ? 'View my hub profile' : '', // P/Member_Hub.html:1755
        };
    }

    /**
     * Whether the member is in the Science Talent Network, from `researchPortalConfig.inTalentNetwork` (a linked MAPS.Person). Set by
     * `InitializeSession` once the access gate resolves; false until then and on a failed read, so the module never shows on a guess.
     */
    private inTalentNetwork = false;

    /**
     * The sidebar's Science Talent Network module (copy verbatim from P/Member_Hub.html:1717): present only for a member who is in the
     * network AND whose app has a profile module to land on — its CTA opens that profile, and a CTA that went nowhere would be worse
     * than no module (the same rule as the identity block's profile link).
     */
    get Promo(): AGUSidebarPromo | null {
        if (!this.inTalentNetwork || !this.HasModule('profile')) return null;
        return STN_PROMO;
    }

    /** The Featured placements' session state; null only where a harness constructs the surface without its providers. */
    private readonly ads = inject(AGUAdSessionService, { optional: true });

    /** The leaderboard over the active screen, first child of the main column (P/Member_Hub.html:1483-1490); null when none applies. */
    get Leaderboard(): AGUAdPlacement | null {
        return this.ads?.First(RaAdScreen(this.location), 'leaderboard') ?? null;
    }

    /** The sidebar rectangle for the active screen, under the Science Talent Network module (P/Member_Hub.html:1440-1448). */
    get SidebarAd(): AGUAdPlacement | null {
        return this.ads?.First(RaAdScreen(this.location), 'sidebar') ?? null;
    }

    protected initDashboard(): void {
        void this.RunSession();
    }

    /** True while `InitializeSession` is running, so a re-entry during the first load does not start a second one. */
    private sessionInFlight = false;

    private async RunSession(): Promise<void> {
        if (this.sessionInFlight) return;
        this.sessionInFlight = true;
        try {
            await this.InitializeSession();
        } finally {
            this.sessionInFlight = false;
        }
    }

    protected loadData(): void {
        // Nothing to fetch at the surface level — each module loads its own data
        // once the readiness gate resolves.
    }

    /** Header nav → move to a module's default view. */
    SelectModule(module: AGUAppModuleDefinition): void {
        this.hostAdapter.NavigateTo({ ModuleKey: module.Key, View: module.DefaultView });
    }

    /**
     * Sidebar account menu → sign out. Drops the memoised member profile first,
     * so a second member signing in on the same tab can never be shown the
     * first one's name, title or organization.
     */
    SignOut(): void {
        this.drawerOpen = false; // the host keeps a left surface detached: an open drawer would greet the next sign-in
        this.api.ClearSessionCache();
        this.hostAdapter.SignOut();
    }

    /** Back to Explorer (sidebar menu, drawer, lock states). Closes the drawer first: the host keeps the left surface detached, so it would still be open on re-entry. */
    ReturnToHost(): void {
        this.drawerOpen = false;
        this.hostAdapter.ReturnToHost();
    }

    /** Whether the active location is a detail view, where the phone shell drops its bottom tabs. */
    get IsDetail(): boolean {
        return IsDetailLocation(this.location);
    }

    /**
     * The crumb's first ancestor while a detail is on screen — what the phone app bar shows in the crumb's place (P/Member_Hub.html
     * :862-868). Null off detail views: a module kept mounted keeps its detail (and that detail's last trail) alive while hidden.
     */
    private get AppBarAncestor(): AGUCrumbItem | null {
        return this.IsDetail ? FirstAncestor(this.crumbTrail) : null;
    }

    /** The app bar's title (`.mtitle`): the origin label on a detail ("Grant Finder", "Home"), the app name elsewhere (P/Member_Hub.html:868). */
    get AppBarTitle(): string {
        return this.AppBarAncestor?.Label ?? this.AppName;
    }

    /** The app bar's back chevron: the first ancestor's bare label on a detail with a trail (the bar names it "Back to {label}"), else null (the AGU mark). */
    get AppBarBackLabel(): string | null {
        return this.AppBarAncestor?.Label ?? null;
    }

    /** App bar back chevron → the crumb's first ancestor (P/Member_Hub.html:864-865, `.mlead` → the back link's href). */
    AppBarBack(): void {
        const location = this.AppBarAncestor?.Location;
        if (location) this.hostAdapter.NavigateTo(location);
    }

    /** App bar AGU mark → Home (P/Member_Hub.html:866), the dashboard module the sidebar's Home item opens; a no-op when the app has none. */
    GoHome(): void {
        const home = this.modules.find((m) => m.Key === RA_MODULE_KEYS.Dashboard);
        if (home) this.SelectModule(home);
    }

    /** App bar avatar → the phone drawer. */
    OpenDrawer(): void {
        this.drawerOpen = true;
    }

    CloseDrawer(): void {
        this.drawerOpen = false;
    }

    /** A module picked in the drawer: go there and close the sheet. */
    SelectModuleFromDrawer(module: AGUAppModuleDefinition): void {
        this.SelectModule(module);
        this.drawerOpen = false;
    }

    /** Focus entered the tucked-away tab bar (keyboard): show it in state as well as on screen (L65). */
    OnTabsRevealed(): void {
        if (!this.tabsHidden) return;
        this.tabsHidden = false;
        this.render.Notify();
    }

    /** The phone tab bar tucks away on a scroll down past 120px and returns on scroll up (P/Member_Hub.html:968-975). */
    private OnSurfaceScroll(): void {
        if (!this.IsPhone) return;
        const y = this.HostElement.nativeElement.scrollTop;
        const hidden = HideTabsOnScroll(this.lastScrollY, y, this.tabsHidden);
        if (Math.abs(y - this.lastScrollY) < TAB_SCROLL_JITTER_PX) return;
        this.lastScrollY = y;
        if (hidden === this.tabsHidden) return;
        this.tabsHidden = hidden;
        this.render.Notify();
    }

    /** Whether this app's resolved composition includes the given module. */
    HasModule(key: string): boolean {
        return this.modules.some((m) => m.Key === key);
    }

    /** Sidebar identity/search affordances → navigate to a specific module + view. */
    OpenModule(key: string, view: string, recordID?: string): void {
        if (!this.HasModule(key)) return;
        this.hostAdapter.NavigateTo(
            recordID ? { ModuleKey: key, View: view, RecordID: recordID } : { ModuleKey: key, View: view }
        );
    }

    /** Sidebar search box submit → the search module, if this app has one. */
    OpenSearch(query: string): void {
        this.OpenModule('search', 'results', query);
    }

    /** Page identity for activity tracking: `module/view`, with the record id on detail views. */
    protected override DescribeLocation(location: AGUAppLocation): UserActivityPageDescriptor {
        return DescribeAppLocation(location);
    }

    /**
     * Mount the module the surface has just moved to, if this is its first
     * visit. Runs inside the base class's location subscription, which already
     * marks the view for check afterwards, so no explicit repaint is needed
     * here.
     */
    protected override OnLocationChanged(location: AGUAppLocation): void {
        const module = this.modules.find((m) => m.Key === location.ModuleKey) ?? null;
        this.mountedModules = WithMountedModule(this.mountedModules, module);

        if (ShouldResetSurfaceScroll(this.previousLocation, location, module?.DefaultView ?? null)) {
            this.HostElement.nativeElement.scrollTop = 0;
        }
        this.previousLocation = location;
    }

    /**
     * The member came back to this surface from another app (B-3, 2026-10-05).
     * MJ re-attaches the cached instance without re-running any lifecycle hook,
     * so everything this surface treats as per-session — the visit ledger, the
     * access mode, the memoised `MyProfile` (sidebar name/role line) — would
     * otherwise live as long as MJ's component cache.
     *
     * A surface that never landed (denied, or an error) re-runs the whole
     * session: the account may have been linked or given a role meanwhile (the
     * suite's "stale role data right after a persona switch"). A landed
     * surface keeps its location and mounted modules and refreshes only the
     * session facts, then tells the modules (`RaCompositionState
     * .SurfaceReentered$`) so an on-screen Home re-loads and records the visit.
     */
    protected override OnSurfaceReentered(): void {
        this.compositionState.ResetVisit();
        this.api.ClearSessionCache();
        this.portalConfig.Clear();
        if (!this.ready) {
            void this.RunSession();
            return;
        }
        void this.RefreshSessionOnReentry();
    }

    /** The landed-surface half of `OnSurfaceReentered`: re-check access, then notify the modules and reload the identity block. */
    private async RefreshSessionOnReentry(): Promise<void> {
        try {
            if (!(await this.CheckAppAccess())) return;
            const access = await this.api.Access();
            this.membership.Set(access);
            if (access.mode === 'denied') {
                this.accessDenied = access.reason;
                this.render.Refresh();
                return;
            }
            if (this.accessDenied !== null) {
                // Denied on an earlier re-entry, linked again now: drop the lock.
                this.accessDenied = null;
                this.render.Refresh();
            }
        } catch (error) {
            // Keep the working surface rather than replacing it with an error
            // over a re-check; the modules' own loads report real failures.
            console.warn(`[ra-surface] could not re-check access on re-entry`, error);
        }
        this.compositionState.NotifySurfaceReentered();
        void this.LoadRoleLine();
    }

    protected override OnQueryParamsChanged(
        params: Record<string, string>,
        _source: 'popstate' | 'deeplink'
    ): void {
        const loc = DecodeLocationParams(params);
        if (loc && this.HasModule(loc.ModuleKey)) this.hostAdapter.ResetTo(loc);
    }

    /**
     * Resolve readiness, then the composition, then entitlement, then the
     * member-portal access gate, then land.
     *
     * Order matters: entitlement is read from MJ metadata, which is only
     * trustworthy after the readiness gate resolves. Resolving it earlier would
     * see an empty role list and lock the user out of their own app. Access is
     * read last, right before landing, because `researchAccess` is a server
     * round-trip and there is no point paying for it if the composition itself
     * failed to resolve.
     */
    private async InitializeSession(): Promise<void> {
        try {
            await this.readyService.WaitForSetup();
            if (!(await this.CheckAppAccess())) return;

            const parsed = ParseAppComposition(this.Config?.dashboard?.UIConfigDetails);
            if (!parsed.Ok) {
                throw new Error(parsed.Error);
            }
            this.composition = parsed.Composition;

            const { Modules, MissingKeys } = ResolveComposedModules(
                parsed.Composition,
                this.registry,
                CurrentUserRoleNames()
            );
            if (MissingKeys.length > 0) {
                // Loud, not silent: a metadata row naming a module whose code is
                // not deployed is a wiring error someone has to see. The app still
                // opens on whatever modules did resolve.
                console.error(
                    `[ra-surface] '${this.AppName}' names ${MissingKeys.length} module(s) with no ` +
                        `registered definition: ${MissingKeys.join(', ')}. ` +
                        `Registered: ${this.registry.map((m) => m.Key).join(', ') || '(none)'}`
                );
            }

            // The member-portal access gate: 'denied' has no AGU member record
            // linked to this account and renders the lock state instead of the
            // shell; 'staff' proceeds with no sidebar role line, since a staff
            // account has no member profile to state a job title or
            // organization from (see `LoadRoleLine`); 'member' proceeds as
            // normal. A transport failure here falls through to the catch below
            // and shows the existing error state, same as a composition failure.
            const access = await this.api.Access();
            this.membership.Set(access);
            if (access.mode === 'denied') {
                this.accessDenied = access.reason;
                this.loadError = null;
                // Repaint explicitly. `this.Zone` is a NoopNgZone on this host
                // (see AGURenderService's header for the framework trace), so
                // `Zone.run` would be a plain function call and would schedule
                // nothing — the state transition has to drive the view itself.
                this.render.Refresh();
                return;
            }
            const landing = PickLandingModule(parsed.Composition, Modules);
            // Read right before landing, same reasoning as `researchAccess`
            // above — a server round trip only worth paying once the
            // composition/entitlement gates already passed. Best-effort: a
            // failed load leaves the Science Talent Network module out
            // rather than blocking the surface.
            const config = await this.LoadPortalConfig();
            await this.ConfigureAds(config);
            this.modules = Modules;
            this.phoneTabs = PhoneTabModules(Modules);
            // Published BEFORE `ready` flips, so the dashboard module — which
            // renders one card per sibling — never mounts against an empty
            // composition and flash an "no modules available" state.
            this.compositionState.Set(Modules);
            this.inTalentNetwork = config?.inTalentNetwork ?? false;
            this.ready = true;
            this.loadError = null;
            // A re-run after a denied first load (`OnSurfaceReentered`) must drop the lock.
            this.accessDenied = null;
            // A deep link (reload, external URL, browser navigation into this
            // tab) beats the landing module — the whole point of mirroring
            // location into the URL is that reload restores where the user was.
            //
            // Read straight from the document's own URL, not
            // `GetQueryParams()`: this surface is a code-based dashboard
            // hosted by ng-explorer-core's `DashboardResource`, and
            // `loadCodeBasedDashboard()` never forwards `Data`/`ParentTabId`
            // down to the instance it creates (only `Config`) — so
            // `Data.Configuration` is always empty here and
            // `GetQueryParams()` would always return `{}`. See
            // `DecodeLocationParamsFromSearch`'s doc comment in
            // `app-location.ts` for the full trace.
            const deepLink = DecodeLocationParamsFromSearch(this.Document.location.search);
            const start =
                deepLink && Modules.some((m) => m.Key === deepLink.ModuleKey)
                    ? deepLink
                    : landing
                      ? { ModuleKey: landing.Key, View: landing.DefaultView }
                      : null;
            if (start) {
                // ResetTo, not NavigateTo: landing is not somewhere the user
                // navigated, so Back must not return them to the blank
                // pre-resolution location. ResetTo also mirrors `start` onto
                // the tab's visible URL (`ExplorerRaHostAdapter.ResetTo`) —
                // see that adapter's doc comment for why the surface writes
                // the URL there (via Angular's `Location` service) rather
                // than through `BaseResourceComponent.UpdateQueryParams()`.
                this.hostAdapter.ResetTo(start);
            }
            // This pass is what mounts the landing module, so it must be an
            // explicit repaint: nothing else will happen on a zoneless host.
            this.render.Refresh();
            // Deliberately NOT awaited, and deliberately after `ready`: the
            // identity block is sidebar chrome, and making the whole surface
            // wait on another round trip to show it would trade the member's
            // time-to-first-screen for a subtitle. Staff load it too (B-20,
            // "real name when known"): `memberName` is the one name rule, and a
            // staff account's MJ user name is real even though it has no member
            // record — its role line simply composes to null.
            void this.LoadRoleLine();
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.ready = false;
            this.accessDenied = null;
            this.loadError = message;
            this.render.Refresh();
        }
    }

    /**
     * Sets `appAccessDenied` from the user's app access, repainting when it changes, and returns
     * whether the session may go on. A check that throws counts as a refusal: the re-entry caller
     * deliberately keeps a working surface on its own failures, and this gate must not inherit that.
     */
    private async CheckAppAccess(): Promise<boolean> {
        let allowed: boolean;
        try {
            allowed = await CurrentUserCanOpenApp(this.Config?.dashboard?.ApplicationID);
        } catch (error) {
            console.error(`[ra-surface] could not check access to '${this.AppName}'; refusing`, error);
            allowed = false;
        }
        if (allowed === !this.appAccessDenied) return allowed;
        this.appAccessDenied = !allowed;
        this.render.Refresh();
        return allowed;
    }

    /**
     * Fill in the sidebar's role line AND identity name from the member's own
     * profile (M-1b: both come off the same `researchMyProfile` round trip,
     * so they always resolve together — no paint where the role line is
     * right and the name lags behind on the generic fallback).
     *
     * Best-effort, like `LoadPortalConfig`: a failure leaves both absent
     * rather than surfacing an error over a decoration. `MyProfile` is memoised
     * per session (see `ResearchApiService`), so this shares its result with the
     * profile page rather than paying for the aggregate twice. The repaint is explicit
     * because this resolves long after the surface's own change-detection pass,
     * on a host where nothing schedules another one (see `AGURenderService`).
     */
    private async LoadRoleLine(): Promise<void> {
        let line: string | null;
        let name: string | null;
        try {
            const profile = await this.api.MyProfile();
            line = ComposeRoleLine(profile.jobTitle, profile.organization);
            name = profile.memberName;
        } catch (error) {
            // Reported, not swallowed — same channel `MissingKeys` uses above.
            // A member with no role line looks identical to a member whose
            // profile call failed, so without this the failure is invisible.
            console.warn(`[ra-surface] could not load the sidebar role line`, error);
            return;
        }
        if (line === this.roleLine && name === this.memberName) return;
        this.roleLine = line;
        this.memberName = name;
        this.render.Refresh();
    }

    /**
     * `researchPortalConfig` for the sidebar's Science Talent Network module and the Featured placements — null on failure, so
     * both are left out rather than the whole surface failing to open.
     */
    private async LoadPortalConfig(): Promise<ResearchPortalConfig | null> {
        try {
            return await this.portalConfig.Load();
        } catch (error) {
            console.warn('[ra-surface] could not load the portal config for the Science Talent Network module and placements', error);
            return null;
        }
    }

    /** Hands `adsEnabled` and the placements to the session state; while ads are on, reads the Home rotation index first (fail-silent). */
    private async ConfigureAds(config: ResearchPortalConfig | null): Promise<void> {
        if (!this.ads) return;
        const enabled = config?.adsEnabled === true;
        this.ads.Configure(enabled, enabled && config ? ToAdPlacements(config.adPlacements) : []);
        if (enabled) await this.ads.PrimeRotation();
    }
}

/** Imported for side effects from the module to prevent tree-shaking the registration. */
export function LoadRaSurface(): void {
    // no-op; the @RegisterClass decorator runs on import
}
